import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Code,
  Group,
  Image,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconShieldCheckered,
  IconShieldLock,
} from '@tabler/icons-react';
import {
  confirmMfaWithToken,
  enrollMfaWithToken,
  loginMfaRequest,
  loginRequest,
} from './auth-api';
import { useAuthStore } from './auth-store';
import { ApiError } from '../lib/api-error';

interface EnrollmentSetup {
  qrDataUrl: string;
  otpauthUrl: string;
}

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);

  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
  const [enrollSetup, setEnrollSetup] = useState<EnrollmentSetup | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  if (user) {
    const to = (location.state as LocationState | undefined)?.from ?? '/';
    return <Navigate to={to} replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await loginRequest(identity, password);
      if ('status' in res && res.status === 'mfa_required') {
        setMfaToken(res.mfa_token);
        setMfaCode('');
        return;
      }
      if ('status' in res && res.status === 'mfa_enrollment_required') {
        setEnrollmentToken(res.enrollment_token);
        // Kick off the secret/QR fetch immediately so the user sees it on the
        // next render — there's no value in making them click another button.
        try {
          const setup = await enrollMfaWithToken(res.enrollment_token);
          setEnrollSetup({ qrDataUrl: setup.qr_data_url, otpauthUrl: setup.otpauth_url });
          setEnrollCode('');
        } catch (err) {
          if (err instanceof ApiError) {
            setError({ code: err.code, message: prettyError(err) });
          }
        }
        return;
      }
      setSession(res.user, res.access_token);
      const to = (location.state as LocationState | undefined)?.from ?? '/';
      navigate(to, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ code: err.code, message: prettyError(err) });
      } else {
        setError({ code: 'network_error', message: 'Could not reach the server. Try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onEnrollSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!enrollmentToken) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await confirmMfaWithToken(enrollmentToken, enrollCode.trim());
      // Show recovery codes ONCE in a blocking modal, then sign the user in
      // and navigate to their original destination.
      const dest = (location.state as LocationState | undefined)?.from ?? '/';
      modals.open({
        title: (
          <Group gap="xs">
            <IconShieldCheckered size={18} />
            <span>Save your recovery codes</span>
          </Group>
        ),
        withCloseButton: false,
        closeOnEscape: false,
        closeOnClickOutside: false,
        children: (
          <RecoveryCodesPanel
            codes={res.recovery_codes}
            onAcknowledge={() => {
              modals.closeAll();
              setSession(res.user, res.access_token);
              navigate(dest, { replace: true });
            }}
          />
        ),
        size: 'md',
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ code: err.code, message: prettyError(err) });
        if (
          err.code === 'enrollment_token_expired' ||
          err.code === 'mfa_reuse_detected' ||
          err.code === 'invalid_enrollment_token'
        ) {
          setEnrollmentToken(null);
          setEnrollSetup(null);
        }
      } else {
        setError({ code: 'network_error', message: 'Could not reach the server. Try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onMfaSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await loginMfaRequest(mfaToken, mfaCode.trim());
      setSession(res.user, res.access_token);
      const to = (location.state as LocationState | undefined)?.from ?? '/';
      navigate(to, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError({ code: err.code, message: prettyError(err) });
        if (err.code === 'mfa_token_expired' || err.code === 'mfa_reuse_detected') {
          setMfaToken(null);
        }
      } else {
        setError({ code: 'network_error', message: 'Could not reach the server. Try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center h="100vh" bg="var(--mantine-color-default-hover)">
      <Paper shadow="md" radius="md" p="xl" w={420} withBorder>
        <Stack gap="md">
          <Box>
            <Title order={2}>Running Title</Title>
            <Text c="dimmed" size="sm">
              {mfaToken
                ? 'Enter the 6-digit code from your authenticator app, or a recovery code'
                : enrollmentToken
                  ? 'Multi-factor authentication enrollment is required for your account'
                  : 'Sign in to continue'}
            </Text>
          </Box>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error.message}
            </Alert>
          )}

          {enrollmentToken ? (
            <Stack gap="sm">
              <Alert icon={<IconShieldLock size={16} />} color="orange" variant="light">
                Your account requires multi-factor authentication. Set it up below to finish
                signing in.
              </Alert>
              {enrollSetup ? (
                <form onSubmit={onEnrollSubmit}>
                  <Stack gap="sm">
                    <Text size="sm">
                      Scan the QR with an authenticator app (1Password, Authy, Google
                      Authenticator, etc.) then enter the 6-digit code.
                    </Text>
                    <Group justify="center" my="xs">
                      <Image src={enrollSetup.qrDataUrl} alt="MFA QR code" w={180} h={180} />
                    </Group>
                    <Text size="xs" c="dimmed">
                      Manual setup key: <Code>{enrollSetup.otpauthUrl}</Code>
                    </Text>
                    <TextInput
                      label="6-digit code from authenticator app"
                      value={enrollCode}
                      onChange={(e) => setEnrollCode(e.currentTarget.value)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      required
                    />
                    <Group justify="space-between">
                      <Anchor
                        size="xs"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setEnrollmentToken(null);
                          setEnrollSetup(null);
                          setError(null);
                        }}
                      >
                        Cancel
                      </Anchor>
                      <Button
                        type="submit"
                        loading={submitting}
                        disabled={enrollCode.trim().length < 6}
                      >
                        Verify and continue
                      </Button>
                    </Group>
                  </Stack>
                </form>
              ) : (
                <Text size="sm" c="dimmed">
                  Loading enrollment…
                </Text>
              )}
            </Stack>
          ) : mfaToken ? (
            <form onSubmit={onMfaSubmit}>
              <Stack gap="sm">
                <TextInput
                  label="Authenticator code"
                  placeholder="123456 or XXXXX-XXXXX"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.currentTarget.value)}
                  required
                  autoFocus
                  inputMode="text"
                  autoComplete="one-time-code"
                />
                <Button type="submit" loading={submitting} fullWidth mt="xs">
                  Verify
                </Button>
                <Anchor
                  size="xs"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setMfaToken(null);
                    setError(null);
                  }}
                  ta="center"
                >
                  Cancel
                </Anchor>
              </Stack>
            </form>
          ) : (
            <form onSubmit={onSubmit}>
              <Stack gap="sm">
                <TextInput
                  label="Username or email"
                  autoComplete="username"
                  value={identity}
                  onChange={(e) => setIdentity(e.currentTarget.value)}
                  required
                  autoFocus
                />
                <PasswordInput
                  label="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required
                />
                <Button type="submit" loading={submitting} fullWidth mt="xs">
                  Sign in
                </Button>
              </Stack>
            </form>
          )}

          <Text size="xs" c="dimmed" ta="center">
            <Anchor component={Link} to="/forgot-password">
              Forgot your password?
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    </Center>
  );
}

function prettyError(err: ApiError): string {
  switch (err.code) {
    case 'invalid_credentials':
      return 'The username/email or password is incorrect.';
    case 'account_inactive':
      return 'This account is inactive. Contact an administrator.';
    case 'account_locked': {
      const until = err.body?.locked_until;
      return until
        ? `Account is locked until ${new Date(until).toLocaleString()}.`
        : 'Account is temporarily locked after too many failed attempts.';
    }
    case 'invalid_code':
      return 'That authenticator code or recovery code is not valid. Try again.';
    case 'invalid_mfa_token':
    case 'mfa_token_expired':
      return 'The MFA challenge expired. Sign in again.';
    case 'mfa_reuse_detected':
      return 'That MFA token has already been used. Sign in again.';
    case 'invalid_enrollment_token':
    case 'enrollment_token_expired':
      return 'The MFA enrollment session expired. Sign in again to start over.';
    case 'mfa_already_enabled':
      return 'MFA is already enabled on this account. Sign in normally.';
    case 'validation_failed':
      return 'Please enter your username/email and password.';
    default:
      return err.message || err.code;
  }
}

function RecoveryCodesPanel({
  codes,
  onAcknowledge,
}: {
  codes: string[];
  onAcknowledge: () => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <Stack gap="sm">
      <Alert color="orange" variant="light">
        These codes are shown <strong>once</strong>. Save them somewhere safe (e.g. a password
        manager). Each one is single-use and lets you sign in if you lose your authenticator.
      </Alert>
      <Card withBorder padding="md">
        <Stack gap={6}>
          {codes.map((c) => (
            <Code key={c} block>
              {c}
            </Code>
          ))}
        </Stack>
      </Card>
      <Group justify="space-between">
        <Button
          variant="light"
          onClick={() => {
            void navigator.clipboard.writeText(codes.join('\n'));
            notifications.show({ title: 'Copied to clipboard', message: '', color: 'pine' });
          }}
        >
          Copy all
        </Button>
        <Button
          color={acknowledged ? 'pine' : 'gray'}
          onClick={() => {
            if (!acknowledged) {
              setAcknowledged(true);
              return;
            }
            onAcknowledge();
          }}
        >
          {acknowledged ? "Yes — I've saved them" : "I've saved these"}
        </Button>
      </Group>
    </Stack>
  );
}
